import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Users, 
  Plus, 
  Search, 
  Shield,
  UserCheck,
  UserX,
  Mail,
  Calendar,
  Edit2,
  Trash2,
  Eye,
  Lock,
  ArrowUpDown,
  FileText,
  ShoppingCart,
  DollarSign,
  Clock,
  Package,
  CheckCircle,
  CreditCard,
  Truck,
  MapPin
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ResetPasswordDialog from './ResetPasswordDialog';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateSort, setDateSort] = useState('newest');
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [customerPayments, setCustomerPayments] = useState([]);
  const [customerWallet, setCustomerWallet] = useState(null);
  const [customerWalletTx, setCustomerWalletTx] = useState([]);
  const [customerReservations, setCustomerReservations] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Check if user has permission for an action
  const hasPermission = (action) => {
    // Admin has all permissions
    if (user?.role === 'admin') return true;
    
    // Check employee permissions
    if (user?.role === 'employee' && user?.permissions) {
      return user.permissions[action] === true;
    }
    
    return false;
  };

  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: '',
    password: '',
    phone: '',
    company: '',
    country: '',
    commission_percentage: 0,
    commission_level: 'agent',
    base_salary_pkr: 0,
    per_unit_commission_yen: 0,
    currency: 'USD',
    source: 'CRM',
    send_email: false,
    permissions: {
      vehicles_view: false,
      vehicles_edit: false,
      orders_view: false,
      orders_edit: false,
      payments_view: false,
      payments_edit: false,
      documents_view: false,
      documents_edit: false,
      reports_view: false,
      users_view: false
    },
    cnic_images: [],
    contract_image: '',
    passport_images: []
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRoleChange = (role) => {
    setFormData({
      ...formData,
      role: role
    });
  };

  const handlePermissionChange = (permission) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permission]: !formData.permissions[permission]
      }
    });
  };

  const handleDocumentUpload = async (e, documentType) => {
    const file = e.target.files[0];
    if (!file || !editingUser) return;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('document_type', documentType);

      const response = await axios.post(
        `${API}/users/${editingUser.id}/upload-document`,
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );

      const fileUrl = response.data.file_url;

      if (documentType === 'cnic') {
        setFormData({ ...formData, cnic_images: [...formData.cnic_images, fileUrl] });
      } else if (documentType === 'contract') {
        setFormData({ ...formData, contract_image: fileUrl });
      } else if (documentType === 'passport') {
        setFormData({ ...formData, passport_images: [...formData.passport_images, fileUrl] });
      }

      toast.success('Document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    }
  };

  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setFormData({
      email: userToEdit.email,
      full_name: userToEdit.full_name,
      role: userToEdit.role,
      password: '',
      phone: userToEdit.phone || '',
      company: userToEdit.company || '',
      country: userToEdit.country || '',
      commission_percentage: userToEdit.commission_percentage || 0,
      commission_level: userToEdit.commission_level || 'agent',
      base_salary_pkr: userToEdit.base_salary_pkr || 0,
      per_unit_commission_yen: userToEdit.per_unit_commission_yen || 0,
      currency: userToEdit.currency || 'USD',
      source: userToEdit.source || 'CRM',
      send_email: false,
      permissions: userToEdit.permissions || {
        vehicles_view: false,
        vehicles_edit: false,
        orders_view: false,
        orders_edit: false,
        payments_view: false,
        payments_edit: false,
        documents_view: false,
        documents_edit: false,
        reports_view: false,
        users_view: false
      },
      cnic_images: userToEdit.cnic_images || [],
      contract_image: userToEdit.contract_image || '',
      passport_images: userToEdit.passport_images || []
    });
    setShowEditDialog(true);
  };

  const handleViewDocuments = (userItem) => {
    const docs = [];
    if (userItem.cnic_images?.length > 0) {
      userItem.cnic_images.forEach((url, idx) => {
        window.open(url, '_blank');
      });
    }
    if (userItem.contract_image) {
      window.open(userItem.contract_image, '_blank');
    }
    if (userItem.passport_images?.length > 0) {
      userItem.passport_images.forEach((url) => {
        window.open(url, '_blank');
      });
    }
    if (!userItem.cnic_images?.length && !userItem.contract_image && !userItem.passport_images?.length) {
      toast.error('No documents available');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    // Validation for required fields
    if (!formData.full_name || !formData.full_name.trim()) {
      toast.error('Full Name is required');
      return;
    }
    
    if (!formData.phone || !formData.phone.trim()) {
      toast.error('Phone Number is required');
      return;
    }
    
    try {
      const updateData = {
        full_name: formData.full_name,
        phone: formData.phone,
        company: formData.company,
        country: formData.country,
        currency: formData.currency,
        commission_percentage: formData.role === 'employee' ? parseFloat(formData.commission_percentage) : 0,
        commission_level: formData.role === 'employee' ? formData.commission_level : null,
        base_salary_pkr: formData.role === 'employee' ? parseFloat(formData.base_salary_pkr) : 0,
        per_unit_commission_yen: formData.role === 'employee' ? parseFloat(formData.per_unit_commission_yen) : 0,
        permissions: formData.role === 'employee' ? formData.permissions : null
      };
      
      await axios.put(`${API}/users/${selectedUser.id}`, updateData);
      toast.success('User updated successfully!');
      setShowEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const fetchCustomerHistory = async (customer) => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch orders for this customer
      const ordersRes = await axios.get(`${API}/search/orders?customer_id=${customer.id}`, { headers });
      const orders = ordersRes.data || [];

      // Fetch all payments for this customer in one request (avoids N+1)
      let allPayments = [];
      try {
        const payRes = await axios.get(`${API}/payments/customer/${customer.id}`, { headers });
        allPayments = Array.isArray(payRes.data) ? payRes.data : [];
      } catch (e) {
        // silent
      }

      setCustomerOrders(orders);
      setCustomerPayments(allPayments);

      // Fetch wallet data
      try {
        const [walletRes, txRes] = await Promise.all([
          axios.get(`${API}/wallets/${customer.id}`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/wallets/${customer.id}/transactions`, { headers }).catch(() => ({ data: [] }))
        ]);
        setCustomerWallet(walletRes.data);
        setCustomerWalletTx(txRes.data || []);
      } catch (e) {
        setCustomerWallet(null);
        setCustomerWalletTx([]);
      }

      // Fetch vehicle reservations (separate collection from orders)
      try {
        const resRes = await axios.get(`${API}/admin/customer/${customer.id}/reservations`, { headers });
        setCustomerReservations(Array.isArray(resRes.data) ? resRes.data : []);
      } catch (e) {
        setCustomerReservations([]);
      }
    } catch (error) {
      console.error('Error fetching customer history:', error);
      toast.error('Failed to load customer history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewHistory = (customer) => {
    setHistoryCustomer(customer);
    setShowHistoryDialog(true);
    fetchCustomerHistory(customer);
  };

  // Build lookup maps from the customer's orders so payments/wallet txns can show vehicle + order info
  const orderById = {};
  const vehicleById = {};
  customerOrders.forEach(o => {
    orderById[o.id] = o;
    (o.vehicles_info || []).forEach(v => { vehicleById[v.id] = v; });
  });

  // Vehicles a payment applies to: its linked vehicle (payment.vehicle_id) if set, else all order vehicles
  const getPaymentVehicles = (payment) => {
    const orderVehicles = orderById[payment.order_id]?.vehicles_info || [];
    if (payment.vehicle_id) {
      const v = vehicleById[payment.vehicle_id] || orderVehicles.find(x => x.id === payment.vehicle_id);
      return v ? [v] : orderVehicles;
    }
    return orderVehicles;
  };

  // Vehicles bought: distinct vehicles across all NON-cancelled orders
  const boughtVehicleIds = new Set();
  customerOrders.forEach(o => {
    if ((o.status || '').toLowerCase() === 'cancelled') return;
    let ids = (o.vehicle_ids && o.vehicle_ids.length)
      ? o.vehicle_ids
      : (o.vehicles_info || []).map(v => v.id).filter(Boolean);
    if (!ids.length && o.vehicle_id) ids = [o.vehicle_id];
    ids.forEach(id => { if (id) boughtVehicleIds.add(id); });
  });
  const boughtVehiclesCount = boughtVehicleIds.size;

  const totalPaidUsd = customerPayments.reduce((sum, p) => sum + (Number(p.amount_usd) || 0), 0);

  const vehicleLabel = (v) => [v.year, v.brand, v.model].filter(Boolean).join(' ') || v.stock_no || 'Vehicle';

  // Full stock line: "HB-184 2017 HONDA ACTY HH5-2011471 SILVER 650 CC AT"
  const vehicleStockLine = (v) => [
    v.stock_no,
    v.year,
    v.brand,
    v.model,
    v.chassis_no,
    v.color,
    v.engine_cc ? `${v.engine_cc} CC` : '',
    v.transmission
  ].filter(Boolean).join(' ');

  // Compute running balance after each wallet txn (chronological), display newest first
  const walletTxWithBalance = (() => {
    const asc = [...customerWalletTx].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    let runUsd = 0, runJpy = 0;
    return asc.map(tx => {
      const isCredit = tx.type === 'credit';
      const sign = isCredit ? 1 : -1;
      runUsd += sign * (tx.amount_usd || 0);
      runJpy += sign * (tx.amount_jpy || 0);
      return { ...tx, _bal_after_usd: runUsd, _bal_after_jpy: runJpy };
    }).reverse();
  })();

  const paymentDisplayDate = (p) => (p.date ? new Date(p.date).toLocaleDateString() : p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '');

  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        toast.success('User deleted successfully!');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for customer role
    if (formData.role === 'customer') {
      if (!formData.full_name || !formData.full_name.trim()) {
        toast.error('Full Name is required for customer users');
        return;
      }
      if (!formData.email || !formData.email.trim()) {
        toast.error('Email is required for customer users');
        return;
      }
      if (!formData.phone || !formData.phone.trim()) {
        toast.error('Phone Number is required for customer users');
        return;
      }
    }
    
    try {
      const userData = {
        ...formData,
        commission_level: formData.role === 'employee' ? formData.commission_level : null,
        permissions: formData.role === 'employee' ? formData.permissions : null
      };
      
      await axios.post(`${API}/users`, userData);
      toast.success('User created successfully!');
      setShowAddDialog(false);
      setFormData({
        email: '',
        full_name: '',
        role: '',
        password: '',
        phone: '',
        company: '',
        country: '',
        commission_percentage: 0,
        commission_level: 'agent',
        base_salary_pkr: 0,
        per_unit_commission_yen: 0,
        currency: 'USD',
        source: 'CRM',
        send_email: false,
        permissions: {
          vehicles_view: false,
          vehicles_edit: false,
          orders_view: false,
          orders_edit: false,
          payments_view: false,
          payments_edit: false,
          documents_view: false,
          documents_edit: false,
          reports_view: false,
          users_view: false
        }
      });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      const message = error.response?.data?.detail || 'Failed to create user';
      toast.error(message);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'employee':
        return 'bg-blue-100 text-blue-800';
      case 'customer':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'employee':
        return <UserCheck className="h-4 w-4" />;
      case 'customer':
        return <Users className="h-4 w-4" />;
      default:
        return <UserX className="h-4 w-4" />;
    }
  };

  const filteredUsers = users.filter(userItem => {
    const matchesSearch = 
      userItem.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userItem.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = !roleFilter || roleFilter === 'all' || userItem.role === roleFilter;
    
    const matchesSource = !sourceFilter || sourceFilter === 'all' || (userItem.source || 'CRM') === sourceFilter;
    
    return matchesSearch && matchesRole && matchesSource;
  }).sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateSort === 'newest' ? dateB - dateA : dateA - dateB;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-64 mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-96"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="user-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-slate-600">Create and manage system user accounts</p>
        </div>
        {hasPermission('users_edit') && (
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700" data-testid="add-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new employee or customer account to the system.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto pr-2">
              <div>
                <Label htmlFor="full_name">
                  Full Name {formData.role === 'customer' && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  required={formData.role === 'customer'}
                />
              </div>

              <div>
                <Label htmlFor="email">
                  Email Address {formData.role === 'customer' && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="user@example.com"
                  required={formData.role === 'customer'}
                />
              </div>

              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={handleRoleChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select user role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">
                      <div className="flex items-center space-x-2">
                        <UserCheck className="h-4 w-4" />
                        <span>Employee</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="customer">
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4" />
                        <span>Customer</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* User Source - Only for customers */}
              {formData.role === 'customer' && (
                <div>
                  <Label htmlFor="source">User Source</Label>
                  <Select 
                    value={formData.source} 
                    onValueChange={(value) => setFormData({...formData, source: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CRM">
                        <div className="flex items-center space-x-2">
                          <Shield className="h-4 w-4" />
                          <span>CRM</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="Website">
                        <div className="flex items-center space-x-2">
                          <Users className="h-4 w-4" />
                          <span>Website</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Website users can only login to the customer portal, not CRM
                  </p>
                </div>
              )}

              {formData.role === 'customer' && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="send_email"
                    checked={formData.send_email}
                    onCheckedChange={(checked) => setFormData({...formData, send_email: checked})}
                  />
                  <Label htmlFor="send_email" className="text-sm cursor-pointer">
                    Send login credentials email to user
                  </Label>
                </div>
              )}

              {/* Employee Permissions */}
              {formData.role === 'employee' && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Shield className="h-4 w-4 text-blue-600" />
                      Employee Permissions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="vehicles_view"
                          checked={formData.permissions.vehicles_view}
                          onCheckedChange={() => handlePermissionChange('vehicles_view')}
                        />
                        <Label htmlFor="vehicles_view" className="text-sm cursor-pointer">
                          Vehicles - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="vehicles_edit"
                          checked={formData.permissions.vehicles_edit}
                          onCheckedChange={() => handlePermissionChange('vehicles_edit')}
                        />
                        <Label htmlFor="vehicles_edit" className="text-sm cursor-pointer">
                          Vehicles - Edit
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="orders_view"
                          checked={formData.permissions.orders_view}
                          onCheckedChange={() => handlePermissionChange('orders_view')}
                        />
                        <Label htmlFor="orders_view" className="text-sm cursor-pointer">
                          Orders - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="orders_edit"
                          checked={formData.permissions.orders_edit}
                          onCheckedChange={() => handlePermissionChange('orders_edit')}
                        />
                        <Label htmlFor="orders_edit" className="text-sm cursor-pointer">
                          Orders - Edit
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="payments_view"
                          checked={formData.permissions.payments_view}
                          onCheckedChange={() => handlePermissionChange('payments_view')}
                        />
                        <Label htmlFor="payments_view" className="text-sm cursor-pointer">
                          Payments - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="payments_edit"
                          checked={formData.permissions.payments_edit}
                          onCheckedChange={() => handlePermissionChange('payments_edit')}
                        />
                        <Label htmlFor="payments_edit" className="text-sm cursor-pointer">
                          Payments - Edit
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="documents_view"
                          checked={formData.permissions.documents_view}
                          onCheckedChange={() => handlePermissionChange('documents_view')}
                        />
                        <Label htmlFor="documents_view" className="text-sm cursor-pointer">
                          Documents - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="documents_edit"
                          checked={formData.permissions.documents_edit}
                          onCheckedChange={() => handlePermissionChange('documents_edit')}
                        />
                        <Label htmlFor="documents_edit" className="text-sm cursor-pointer">
                          Documents - Edit
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="reports_view"
                          checked={formData.permissions.reports_view}
                          onCheckedChange={() => handlePermissionChange('reports_view')}
                        />
                        <Label htmlFor="reports_view" className="text-sm cursor-pointer">
                          Reports - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="users_view"
                          checked={formData.permissions.users_view}
                          onCheckedChange={() => handlePermissionChange('users_view')}
                        />
                        <Label htmlFor="users_view" className="text-sm cursor-pointer">
                          Users - View
                        </Label>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div>
                <Label htmlFor="phone">
                  Phone {formData.role === 'customer' ? <span className="text-red-500">*</span> : '(Optional)'}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone number"
                  required={formData.role === 'customer'}
                />
              </div>

              <div>
                <Label htmlFor="company">Company (Optional)</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Company name"
                />
              </div>

              <div>
                <Label htmlFor="country">Country (Optional)</Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Customer's country"
                />
              </div>

              {/* Currency Selection - Only for customers */}
              {formData.role === 'customer' && (
                <div>
                  <Label htmlFor="currency">
                    Preferred Currency <span className="text-red-500">*</span>
                  </Label>
                  <Select 
                    value={formData.currency} 
                    onValueChange={(value) => setFormData({...formData, currency: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">
                        <div className="flex items-center space-x-2">
                          <span>💵</span>
                          <span>USD ($)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="YEN">
                        <div className="flex items-center space-x-2">
                          <span>¥</span>
                          <span>YEN (¥)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    All payment notifications will be sent in this currency
                  </p>
                </div>
              )}

              {/* Commission Settings - Only for employees */}
              {formData.role === 'employee' && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="commission_level">Commission Level</Label>
                    <Select 
                      value={formData.commission_level} 
                      onValueChange={(value) => setFormData({...formData, commission_level: value})}
                    >
                      <SelectTrigger id="commission_level">
                        <SelectValue placeholder="Select commission level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">
                          <div className="flex items-center space-x-2">
                            <span>🤝</span>
                            <span>Agent</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="manager">
                          <div className="flex items-center space-x-2">
                            <span>📊</span>
                            <span>Manager (37% of profit)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="partner">
                          <div className="flex items-center space-x-2">
                            <span>🏛️</span>
                            <span>Partner (45% of profit)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Agent = base salary + per-unit commission; Manager/Partner = % of profit
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="commission_percentage">Commission Percentage (%)</Label>
                    <Input
                      id="commission_percentage"
                      name="commission_percentage"
                      type="number"
                      value={formData.commission_percentage}
                      onChange={handleInputChange}
                      placeholder="e.g., 5 for 5%"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Legacy flat % of profit — only used when no level-specific formula applies
                    </p>
                  </div>

                  {formData.commission_level === 'agent' && (
                    <>
                      <div>
                        <Label htmlFor="base_salary_pkr">Monthly Base Salary (PKR)</Label>
                        <Input
                          id="base_salary_pkr"
                          name="base_salary_pkr"
                          type="number"
                          value={formData.base_salary_pkr}
                          onChange={handleInputChange}
                          placeholder="e.g., 35000"
                          min="0"
                          step="1000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="per_unit_commission_yen">Commission per Unit (JPY)</Label>
                        <Input
                          id="per_unit_commission_yen"
                          name="per_unit_commission_yen"
                          type="number"
                          value={formData.per_unit_commission_yen}
                          onChange={handleInputChange}
                          placeholder="e.g., 20000"
                          min="0"
                          step="1000"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Multiplied by delivered units in the month, then converted at the settlement rate
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter password"
                  required
                  minLength="6"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user information and permissions.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4 overflow-y-auto pr-2">
              <div>
                <Label htmlFor="edit_full_name">Full Name*</Label>
                <Input
                  id="edit_full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit_email">Email*</Label>
                <Input
                  id="edit_email"
                  name="email"
                  type="email"
                  value={formData.email}
                  disabled
                  className="bg-slate-100"
                />
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>

              <div>
                <Label htmlFor="edit_role">Role</Label>
                <Input
                  id="edit_role"
                  value={formData.role}
                  disabled
                  className="bg-slate-100 capitalize"
                />
                <p className="text-xs text-slate-500 mt-1">Role cannot be changed</p>
              </div>

              {/* User Source - Only for customers */}
              {formData.role === 'customer' && (
                <div>
                  <Label htmlFor="edit_source">User Source</Label>
                  <Select 
                    value={formData.source || 'CRM'} 
                    onValueChange={(value) => setFormData({...formData, source: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CRM">CRM</SelectItem>
                      <SelectItem value="Website">Website</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Website users cannot login to CRM
                  </p>
                </div>
              )}

              {/* Employee Permissions */}
              {formData.role === 'employee' && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Shield className="h-4 w-4 text-blue-600" />
                      Employee Permissions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_vehicles_view"
                          checked={formData.permissions.vehicles_view}
                          onCheckedChange={() => handlePermissionChange('vehicles_view')}
                        />
                        <Label htmlFor="edit_vehicles_view" className="text-sm cursor-pointer">
                          Vehicles - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_vehicles_edit"
                          checked={formData.permissions.vehicles_edit}
                          onCheckedChange={() => handlePermissionChange('vehicles_edit')}
                        />
                        <Label htmlFor="edit_vehicles_edit" className="text-sm cursor-pointer">
                          Vehicles - Edit
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_orders_view"
                          checked={formData.permissions.orders_view}
                          onCheckedChange={() => handlePermissionChange('orders_view')}
                        />
                        <Label htmlFor="edit_orders_view" className="text-sm cursor-pointer">
                          Orders - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_orders_edit"
                          checked={formData.permissions.orders_edit}
                          onCheckedChange={() => handlePermissionChange('orders_edit')}
                        />
                        <Label htmlFor="edit_orders_edit" className="text-sm cursor-pointer">
                          Orders - Edit
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_payments_view"
                          checked={formData.permissions.payments_view}
                          onCheckedChange={() => handlePermissionChange('payments_view')}
                        />
                        <Label htmlFor="edit_payments_view" className="text-sm cursor-pointer">
                          Payments - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_payments_edit"
                          checked={formData.permissions.payments_edit}
                          onCheckedChange={() => handlePermissionChange('payments_edit')}
                        />
                        <Label htmlFor="edit_payments_edit" className="text-sm cursor-pointer">
                          Payments - Edit
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_documents_view"
                          checked={formData.permissions.documents_view}
                          onCheckedChange={() => handlePermissionChange('documents_view')}
                        />
                        <Label htmlFor="edit_documents_view" className="text-sm cursor-pointer">
                          Documents - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_documents_edit"
                          checked={formData.permissions.documents_edit}
                          onCheckedChange={() => handlePermissionChange('documents_edit')}
                        />
                        <Label htmlFor="edit_documents_edit" className="text-sm cursor-pointer">
                          Documents - Edit
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_reports_view"
                          checked={formData.permissions.reports_view}
                          onCheckedChange={() => handlePermissionChange('reports_view')}
                        />
                        <Label htmlFor="edit_reports_view" className="text-sm cursor-pointer">
                          Reports - View
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="edit_users_view"
                          checked={formData.permissions.users_view}
                          onCheckedChange={() => handlePermissionChange('users_view')}
                        />
                        <Label htmlFor="edit_users_view" className="text-sm cursor-pointer">
                          Users - View
                        </Label>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Employee Documents - Only for employees */}
              {formData.role === 'employee' && (
                <Card className="bg-green-50 border-green-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="h-4 w-4 text-green-600" />
                      Employee Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* CNIC Images */}
                    <div>
                      <Label>CNIC Images</Label>
                      <div className="mt-2">
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => handleDocumentUpload(e, 'cnic')}
                          className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-100 file:text-green-700 hover:file:bg-green-200"
                        />
                        {formData.cnic_images.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {formData.cnic_images.map((url, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border">
                                <span className="truncate flex-1">CNIC Image {idx + 1}</span>
                                <Button variant="ghost" size="sm" onClick={() => window.open(url, '_blank')}>
                                  View
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contract Image */}
                    <div>
                      <Label>Contract Image</Label>
                      <div className="mt-2">
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => handleDocumentUpload(e, 'contract')}
                          className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-100 file:text-green-700 hover:file:bg-green-200"
                        />
                        {formData.contract_image && (
                          <div className="mt-2 flex items-center justify-between text-sm bg-white p-2 rounded border">
                            <span className="truncate flex-1">Contract Document</span>
                            <Button variant="ghost" size="sm" onClick={() => window.open(formData.contract_image, '_blank')}>
                              View
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Passport Images */}
                    <div>
                      <Label>Passport Images</Label>
                      <div className="mt-2">
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => handleDocumentUpload(e, 'passport')}
                          className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-100 file:text-green-700 hover:file:bg-green-200"
                        />
                        {formData.passport_images.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {formData.passport_images.map((url, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border">
                                <span className="truncate flex-1">Passport Image {idx + 1}</span>
                                <Button variant="ghost" size="sm" onClick={() => window.open(url, '_blank')}>
                                  View
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div>
                <Label htmlFor="edit_phone">Phone*</Label>
                <Input
                  id="edit_phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone number"
                />
              </div>

              <div>
                <Label htmlFor="edit_company">Company (Optional)</Label>
                <Input
                  id="edit_company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Company name"
                />
              </div>

              <div>
                <Label htmlFor="edit_country">Country (Optional)</Label>
                <Input
                  id="edit_country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Customer's country"
                />
              </div>

              {/* Currency Selection - Only for customers */}
              {formData.role === 'customer' && (
                <div>
                  <Label htmlFor="edit_currency">Preferred Currency*</Label>
                  <Select 
                    value={formData.currency || 'USD'} 
                    onValueChange={(value) => setFormData({...formData, currency: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">
                        <div className="flex items-center space-x-2">
                          <span>💵</span>
                          <span>USD ($)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="YEN">
                        <div className="flex items-center space-x-2">
                          <span>¥</span>
                          <span>YEN (¥)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    All payment notifications will be sent in this currency
                  </p>
                </div>
              )}

              {/* Commission Settings - Only for employees */}
              {formData.role === 'employee' && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="edit_commission_level">Commission Level</Label>
                    <Select 
                      value={formData.commission_level} 
                      onValueChange={(value) => setFormData({...formData, commission_level: value})}
                    >
                      <SelectTrigger id="edit_commission_level">
                        <SelectValue placeholder="Select commission level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">
                          <div className="flex items-center space-x-2">
                            <span>🤝</span>
                            <span>Agent</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="manager">
                          <div className="flex items-center space-x-2">
                            <span>📊</span>
                            <span>Manager (37% of profit)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="partner">
                          <div className="flex items-center space-x-2">
                            <span>🏛️</span>
                            <span>Partner (45% of profit)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Agent = base salary + per-unit commission; Manager/Partner = % of profit
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="edit_commission_percentage">Commission Percentage (%)</Label>
                    <Input
                      id="edit_commission_percentage"
                      name="commission_percentage"
                      type="number"
                      value={formData.commission_percentage}
                      onChange={handleInputChange}
                      placeholder="e.g., 5 for 5%"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Legacy flat % of profit — only used when no level-specific formula applies
                    </p>
                  </div>

                  {formData.commission_level === 'agent' && (
                    <>
                      <div>
                        <Label htmlFor="edit_base_salary_pkr">Monthly Base Salary (PKR)</Label>
                        <Input
                          id="edit_base_salary_pkr"
                          name="base_salary_pkr"
                          type="number"
                          value={formData.base_salary_pkr}
                          onChange={handleInputChange}
                          placeholder="e.g., 35000"
                          min="0"
                          step="1000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_per_unit_commission_yen">Commission per Unit (JPY)</Label>
                        <Input
                          id="edit_per_unit_commission_yen"
                          name="per_unit_commission_yen"
                          type="number"
                          value={formData.per_unit_commission_yen}
                          onChange={handleInputChange}
                          placeholder="e.g., 20000"
                          min="0"
                          step="1000"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Multiplied by delivered units in the month, then converted at the settlement rate
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditDialog(false);
                    setSelectedUser(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Update User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Users</p>
                <p className="text-2xl font-bold text-slate-900">{users.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">CRM Employees</p>
                <p className="text-2xl font-bold text-slate-900">
                  {users.filter(u => u.role === 'employee' || u.role === 'admin').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">CRM Customers</p>
                <p className="text-2xl font-bold text-slate-900">
                  {users.filter(u => u.role === 'customer' && u.source !== 'Website').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Website Customers</p>
                <p className="text-2xl font-bold text-slate-900">
                  {users.filter(u => u.source === 'Website').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Website Customers Section */}
      <Card className="border-orange-200">
        <CardHeader className="bg-orange-50">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-orange-600" />
            Website Customers ({users.filter(u => u.source === 'Website' && (!searchTerm || u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()))).length})
          </CardTitle>
          <CardDescription>Users registered from the public website</CardDescription>
        </CardHeader>
        <CardContent>
          {users.filter(u => u.source === 'Website').length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:text-red-600"
                      onClick={() => setDateSort(dateSort === 'newest' ? 'oldest' : 'newest')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users
                    .filter(u => u.source === 'Website' && (!searchTerm || u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase())))
                    .sort((a, b) => {
                      const dateA = new Date(a.created_at || 0);
                      const dateB = new Date(b.created_at || 0);
                      return dateSort === 'newest' ? dateB - dateA : dateA - dateB;
                    })
                    .map((userItem) => (
                    <TableRow key={userItem.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-orange-200 to-orange-300 rounded-full flex items-center justify-center">
                            <span className="text-orange-700 font-medium text-sm">
                              {userItem.full_name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{userItem.full_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <span className="text-slate-600">{userItem.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getRoleBadgeColor(userItem.role)} flex items-center space-x-1 w-fit`}>
                          {getRoleIcon(userItem.role)}
                          <span className="capitalize">{userItem.role}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {userItem.role === 'customer' ? (
                          <Badge variant="outline" className="flex items-center space-x-1 w-fit">
                            <span>{userItem.currency === 'YEN' ? '¥' : '$'}</span>
                            <span>{userItem.currency || 'USD'}</span>
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1 w-fit">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <span className="text-slate-600 text-sm">{userItem.country || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={userItem.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {userItem.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2 text-slate-500">
                          <Calendar className="h-4 w-4" />
                          <span className="text-sm">
                            {new Date(userItem.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {hasPermission('users_edit') && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(userItem);
                                  setShowResetPasswordDialog(true);
                                }}
                                title="Reset Password"
                              >
                                <Lock className="h-4 w-4" />
                              </Button>
                              {(userItem.role === 'employee' || userItem.role === 'admin') && (userItem.cnic_images?.length > 0 || userItem.contract_image || userItem.passport_images?.length > 0) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewDocuments(userItem)}
                                  title="View Documents"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewHistory(userItem)}
                                title="View Orders & Payments"
                              >
                                <ShoppingCart className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditUser(userItem)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteUser(userItem.id, userItem.full_name)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No website customers yet</p>
              <p className="text-sm">Customers who register from the public website will appear here</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CRM Employees Section */}
      <Card className="border-blue-200">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            CRM Employees ({users.filter(u => (u.role === 'employee' || u.role === 'admin') && (!searchTerm || u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()))).length})
          </CardTitle>
          <CardDescription>Admin and employee users for CRM access</CardDescription>
        </CardHeader>
        <CardContent>
          {users.filter(u => u.role === 'employee' || u.role === 'admin').length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:text-red-600"
                      onClick={() => setDateSort(dateSort === 'newest' ? 'oldest' : 'newest')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users
                    .filter(u => (u.role === 'employee' || u.role === 'admin') && (!searchTerm || u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase())))
                    .sort((a, b) => {
                      const dateA = new Date(a.created_at || 0);
                      const dateB = new Date(b.created_at || 0);
                      return dateSort === 'newest' ? dateB - dateA : dateA - dateB;
                    })
                    .map((userItem) => (
                    <TableRow key={userItem.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-200 to-blue-300 rounded-full flex items-center justify-center">
                            <span className="text-blue-700 font-medium text-sm">
                              {userItem.full_name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{userItem.full_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <span className="text-slate-600">{userItem.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getRoleBadgeColor(userItem.role)} flex items-center space-x-1 w-fit`}>
                          {getRoleIcon(userItem.role)}
                          <span className="capitalize">{userItem.role}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={userItem.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {userItem.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2 text-slate-500">
                          <Calendar className="h-4 w-4" />
                          <span className="text-sm">
                            {new Date(userItem.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {hasPermission('users_edit') && (
                            <>
                              {(userItem.role === 'employee' || userItem.role === 'admin') && (userItem.cnic_images?.length > 0 || userItem.contract_image || userItem.passport_images?.length > 0) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewDocuments(userItem)}
                                  title="View Documents"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditUser(userItem)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(userItem);
                                  setShowResetPasswordDialog(true);
                                }}
                                title="Reset Password"
                              >
                                <Lock className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteUser(userItem.id, userItem.full_name)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Shield className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No employees yet</p>
              <p className="text-sm">Create employee accounts to manage the CRM</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CRM Customers Section */}
      <Card className="border-green-200">
        <CardHeader className="bg-green-50">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-green-600" />
            CRM Customers ({users.filter(u => u.role === 'customer' && u.source !== 'Website' && (!searchTerm || u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()))).length})
          </CardTitle>
          <CardDescription>Customer accounts created in CRM</CardDescription>
        </CardHeader>
        <CardContent>
          {users.filter(u => u.role === 'customer' && u.source !== 'Website').length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:text-red-600"
                      onClick={() => setDateSort(dateSort === 'newest' ? 'oldest' : 'newest')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users
                    .filter(u => u.role === 'customer' && u.source !== 'Website' && (!searchTerm || u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase())))
                    .sort((a, b) => {
                      const dateA = new Date(a.created_at || 0);
                      const dateB = new Date(b.created_at || 0);
                      return dateSort === 'newest' ? dateB - dateA : dateA - dateB;
                    })
                    .map((userItem) => (
                    <TableRow key={userItem.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-green-200 to-green-300 rounded-full flex items-center justify-center">
                            <span className="text-green-700 font-medium text-sm">
                              {userItem.full_name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{userItem.full_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <span className="text-slate-600">{userItem.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getRoleBadgeColor(userItem.role)} flex items-center space-x-1 w-fit`}>
                          {getRoleIcon(userItem.role)}
                          <span className="capitalize">{userItem.role}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="flex items-center space-x-1 w-fit">
                          <span>{userItem.currency === 'YEN' ? '¥' : '$'}</span>
                          <span>{userItem.currency || 'USD'}</span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1 w-fit">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <span className="text-slate-600 text-sm">{userItem.country || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={userItem.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {userItem.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2 text-slate-500">
                          <Calendar className="h-4 w-4" />
                          <span className="text-sm">
                            {new Date(userItem.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {hasPermission('users_edit') && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(userItem);
                                  setShowResetPasswordDialog(true);
                                }}
                                title="Reset Password"
                              >
                                <Lock className="h-4 w-4" />
                              </Button>
                              {(userItem.role === 'employee' || userItem.role === 'admin') && (userItem.cnic_images?.length > 0 || userItem.contract_image || userItem.passport_images?.length > 0) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewDocuments(userItem)}
                                  title="View Documents"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewHistory(userItem)}
                                title="View Orders & Payments"
                              >
                                <ShoppingCart className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditUser(userItem)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteUser(userItem.id, userItem.full_name)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No CRM customers yet</p>
              <p className="text-sm">Create customer accounts in CRM</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={(open) => { if (!open) { setShowHistoryDialog(false); setHistoryCustomer(null); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {historyCustomer ? `${historyCustomer.full_name}'s History` : 'Customer History'}
            </DialogTitle>
          </DialogHeader>
          {historyCustomer && !historyLoading && (
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border bg-slate-50 p-3 text-center">
                <div className="text-2xl font-bold text-slate-900">{boughtVehiclesCount}</div>
                <div className="text-xs text-slate-500 mt-1">Vehicles Bought</div>
              </div>
              <div className="rounded-lg border bg-slate-50 p-3 text-center">
                <div className="text-2xl font-bold text-slate-900">{customerPayments.length}</div>
                <div className="text-xs text-slate-500 mt-1">Payments Made</div>
              </div>
              <div className="rounded-lg border bg-slate-50 p-3 text-center">
                <div className="text-2xl font-bold text-green-600">${totalPaidUsd.toLocaleString()}</div>
                <div className="text-xs text-slate-500 mt-1">Total Paid (USD)</div>
              </div>
            </div>
          )}
          {historyCustomer && (
            <Tabs defaultValue="orders" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="orders" className="flex-1">
                  <Package className="h-4 w-4 mr-2" />
                  Orders ({customerOrders.length})
                </TabsTrigger>
                <TabsTrigger value="payments" className="flex-1">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Payments ({customerPayments.length})
                </TabsTrigger>
                <TabsTrigger value="wallet" className="flex-1">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Wallet
                </TabsTrigger>
                <TabsTrigger value="reservations" className="flex-1">
                  <Calendar className="h-4 w-4 mr-2" />
                  User History ({customerReservations.length})
                </TabsTrigger>
              </TabsList>

              {/* Orders Tab */}
              <TabsContent value="orders" className="space-y-4 mt-4">
                {historyLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                    <p className="text-slate-500 mt-2">Loading orders...</p>
                  </div>
                ) : customerOrders.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No orders found for this customer</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customerOrders.map((order) => {
                      const totalUsd = order.total_amount_usd || 0;
                      const totalJpy = order.total_amount_jpy || 0;
                      const orderPayments = customerPayments.filter(p => p.order_id === order.id);
                      const paidUsd = orderPayments.reduce((sum, p) => sum + (p.amount_usd || 0), 0);
                      const paidJpy = orderPayments.reduce((sum, p) => sum + (p.amount_jpy || 0), 0);
                      const balanceUsd = Math.max(0, totalUsd - paidUsd);
                      const balanceJpy = Math.max(0, totalJpy - paidJpy);
                      const paidPct = totalUsd > 0 ? Math.min(100, Math.round((paidUsd / totalUsd) * 100)) : 0;
                      const orderDate = order.order_date ? new Date(order.order_date).toLocaleDateString() : order.created_at ? new Date(order.created_at).toLocaleDateString() : '';

                      return (
                        <Card key={order.id} className="border border-slate-200">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={`${order.status === 'delivered' ? 'bg-green-100 text-green-700' : order.status === 'shipped' || order.status === 'on_the_way' ? 'bg-blue-100 text-blue-700' : order.status === 'purchased' ? 'bg-yellow-100 text-yellow-700' : order.status === 'reserved' ? 'bg-purple-100 text-purple-700' : order.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'} text-xs`}>
                                    {order.status || 'Pending'}
                                  </Badge>
                                  <Badge variant="outline" className={order.payment_status === 'paid' ? 'bg-green-50 text-green-700' : order.payment_status === 'partial' ? 'bg-orange-50 text-orange-700' : 'bg-gray-50'}>
                                    {order.payment_status || 'pending'}
                                  </Badge>
                                  {order.exchange_rate && (
                                    <span className="text-[11px] text-slate-400">@{order.exchange_rate}/USD</span>
                                  )}
                                  <span className="text-[11px] text-slate-400 ml-auto">{orderDate}</span>
                                </div>
                                {order.vehicles_info?.length > 0 ? (
                                  <div className="mt-2 space-y-2">
                                    {order.vehicles_info.map((v) => (
                                      <div key={v.id} className="flex items-start gap-3">
                                        <div className="relative h-14 w-14 rounded-md overflow-hidden bg-slate-100 shrink-0 border border-slate-200">
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <Truck className="h-6 w-6 text-slate-300" />
                                          </div>
                                          {v.image ? (
                                            <img src={v.image} alt={vehicleLabel(v)} className="relative h-full w-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                                          ) : null}
                                        </div>
                                        <div className="min-w-0">
                                          <p className="font-medium text-sm text-slate-900 leading-snug break-words">{vehicleStockLine(v)}</p>
                                          {(v.etd || v.eta) && (
                                            <p className="text-xs text-slate-500 mt-0.5">
                                              {v.etd ? <>ETD: {v.etd}</> : null}
                                              {v.etd && v.eta ? ' · ' : null}
                                              {v.eta ? <>ETA: {v.eta}</> : null}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : order.vehicle_ids?.length > 0 ? (
                                  <p className="text-xs text-slate-500 mt-2">— {order.vehicle_ids.length} vehicle(s)</p>
                                ) : null}
                              </div>
                              <div className="text-right shrink-0">
                                <p className="font-semibold text-sm">${totalUsd.toLocaleString()}</p>
                                {totalJpy > 0 && <p className="text-xs text-slate-500">¥{totalJpy.toLocaleString()}</p>}
                                <div className="mt-1 text-[11px] space-y-0.5">
                                  {paidUsd > 0 && <p className="text-green-700">Paid ${paidUsd.toLocaleString()}{paidJpy > 0 ? ` / ¥${paidJpy.toLocaleString()}` : ''}</p>}
                                  {balanceUsd > 0 && <p className="text-orange-600">${balanceUsd.toLocaleString()} due{balanceJpy > 0 ? ` / ¥${balanceJpy.toLocaleString()}` : ''}</p>}
                                </div>
                                {order.payment_due_date && <p className="text-[11px] text-slate-400 mt-1">Due {new Date(order.payment_due_date).toLocaleDateString()}</p>}
                              </div>
                            </div>
                            {totalUsd > 0 && (
                              <div className="w-full bg-gray-100 rounded-full h-1.5">
                                <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${paidPct}%` }} />
                              </div>
                            )}
                            {order.notes && <p className="text-xs text-slate-400 mt-2 italic">{order.notes}</p>}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Payments Tab */}
              <TabsContent value="payments" className="space-y-4 mt-4">
                {historyLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                    <p className="text-slate-500 mt-2">Loading payments...</p>
                  </div>
                ) : customerPayments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No payments found for this customer</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {customerPayments.map((payment, idx) => {
                      const pVehicles = getPaymentVehicles(payment);
                      return (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="bg-blue-50 rounded-full p-2">
                              <CreditCard className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {payment.payment_method || 'Payment'}
                                {payment.payment_number ? ` #${payment.payment_number}` : ''}
                              </p>
                              <p className="text-xs text-slate-500">
                                {paymentDisplayDate(payment)}
                                {payment.order_id && ` — Order #${payment.order_id.slice(-8).toUpperCase()}`}
                              </p>
                              {pVehicles.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {pVehicles.map((v) => (
                                    <Badge key={v.id} variant="outline" className="bg-white text-slate-700 text-xs font-normal">
                                      <Truck className="h-3 w-3 mr-1 text-slate-400" />
                                      {vehicleLabel(v)}
                                      {v.stock_no ? ` · ${v.stock_no}` : ''}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-slate-500">
                                {payment.transaction_ref && <span>Ref: {payment.transaction_ref}</span>}
                                {payment.remitter_name && <span>From: {payment.remitter_name}</span>}
                                {payment.country && <span>{payment.country}</span>}
                              </div>
                              {payment.is_pipeline && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs mt-1">Pipeline — email pending</Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-semibold text-green-700">${(payment.amount_usd || 0).toLocaleString()}</p>
                            {(payment.amount_jpy || 0) > 0 && <p className="text-xs text-slate-500">¥{(payment.amount_jpy || 0).toLocaleString()}</p>}
                            <Badge className={`${payment.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} text-xs mt-1`}>
                              {payment.status || 'completed'}
                            </Badge>
                          </div>
                        </div>
                        {payment.notes && <p className="text-[11px] text-slate-400 mt-2 italic">{payment.notes}</p>}
                      </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Wallet Tab */}
              <TabsContent value="wallet" className="space-y-4 mt-4">
                {historyLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                    <p className="text-slate-500 mt-2">Loading wallet...</p>
                  </div>
                ) : !customerWallet ? (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No wallet found for this customer</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Balance Card */}
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-emerald-900">Current Balance</p>
                          <p className="text-2xl font-bold text-emerald-700">
                            ${(customerWallet.balance_usd || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                          </p>
                          {customerWallet.balance_jpy > 0 && (
                            <p className="text-sm text-emerald-600">¥{customerWallet.balance_jpy.toLocaleString()}</p>
                          )}
                        </div>
                        <div className="text-right text-xs text-emerald-700">
                          {(() => {
                            const totalCreditUsd = customerWalletTx.reduce((s, t) => s + (t.type === 'credit' ? (t.amount_usd || 0) : 0), 0);
                            const totalDebitUsd = customerWalletTx.reduce((s, t) => s + (t.type === 'debit' ? (t.amount_usd || 0) : 0), 0);
                            return (
                              <div className="space-y-1">
                                <p>Credited: <span className="font-semibold">${totalCreditUsd.toLocaleString(undefined, {minimumFractionDigits: 2})}</span></p>
                                <p>Deducted: <span className="font-semibold">${totalDebitUsd.toLocaleString(undefined, {minimumFractionDigits: 2})}</span></p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Transaction History */}
                    {customerWalletTx.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-900">Transaction History</p>
                        {walletTxWithBalance.map((tx) => {
                          const isCredit = tx.type === 'credit';
                          const linkedOrder = tx.order_id ? orderById[tx.order_id] : null;
                          const linkedVehicle = tx.vehicle_id ? vehicleById[tx.vehicle_id] : null;
                          return (
                            <div key={tx.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-medium text-slate-900">
                                    {isCredit ? 'Credit' : 'Debit'}
                                    {tx.description && <span className="font-normal text-slate-500"> — {tx.description}</span>}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {new Date(tx.created_at).toLocaleString()}
                                  </p>
                                  {!isCredit && (linkedOrder || linkedVehicle) && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                      {linkedVehicle && (
                                        <Badge variant="outline" className="bg-white text-slate-700 text-xs font-normal">
                                          <Truck className="h-3 w-3 mr-1 text-slate-400" />
                                          {vehicleLabel(linkedVehicle)}
                                          {linkedVehicle.stock_no ? ` · ${linkedVehicle.stock_no}` : ''}
                                        </Badge>
                                      )}
                                      {linkedOrder && (
                                        <Badge variant="outline" className="bg-white text-slate-700 text-xs font-normal">
                                          Order #{linkedOrder.id.slice(-8).toUpperCase()}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right shrink-0">
                                  <p className={`font-semibold ${isCredit ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {isCredit ? '+' : '-'}
                                    {tx.amount_usd > 0 && `$${tx.amount_usd.toLocaleString(undefined, {minimumFractionDigits: 2})}`}
                                    {tx.amount_jpy > 0 && `¥${tx.amount_jpy.toLocaleString()}`}
                                  </p>
                                  <p className="text-xs text-slate-500">Bal: ${(tx._bal_after_usd || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Reservations Tab */}
              <TabsContent value="reservations" className="space-y-4 mt-4">
                {historyLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                    <p className="text-slate-500 mt-2">Loading reservations...</p>
                  </div>
                ) : customerReservations.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No reservations found for this customer</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customerReservations.map((res) => {
                      const v = res.vehicle_info || {};
                      const img = Array.isArray(v.images) && v.images.length > 0 ? v.images[0] : null;
                      const resDate = res.created_at ? new Date(res.created_at).toLocaleString() : '';
                      const price = v.public_price_usd ?? res.vehicle_details?.price_usd ?? null;
                      return (
                        <Card key={res.id} className="border border-slate-200">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="relative w-16 h-16 shrink-0 rounded-md bg-slate-100 overflow-hidden border border-slate-200">
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Truck className="h-6 w-6 text-slate-300" />
                                </div>
                                {img ? (
                                  <img src={img} alt={vehicleStockLine(v)} className="relative w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                                ) : null}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <Badge className={`${res.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : res.status === 'confirmed' ? 'bg-green-100 text-green-700' : res.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'} text-xs`}>
                                    {res.status || 'pending'}
                                  </Badge>
                                  <span className="text-sm font-medium text-slate-900">{vehicleStockLine(v) || v.stock_no || 'Vehicle'}</span>
                                </div>
                                <p className="text-xs text-slate-500">
                                  Reserved {resDate}
                                  {price != null && price !== '' && <span className="ml-2">· ${Number(price).toLocaleString()}</span>}
                                </p>
                                <div className="flex flex-wrap gap-1 mt-1.5 text-xs">
                                  {v.status && (
                                    <Badge variant="outline" className="bg-white text-slate-700 font-normal">Vehicle: {v.status}</Badge>
                                  )}
                                  {res.payment_preference && (
                                    <Badge variant="outline" className="bg-white text-slate-700 font-normal">{res.payment_preference}</Badge>
                                  )}
                                </div>
                                {(res.customer_phone || res.customer_country || res.customer_city) && (
                                  <p className="text-[11px] text-slate-400 mt-1.5">
                                    {[res.customer_phone, res.customer_city, res.customer_country].filter(Boolean).join(' · ')}
                                  </p>
                                )}
                                {res.additional_notes && (
                                  <p className="text-[11px] text-slate-400 mt-1 italic">{res.additional_notes}</p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      {showResetPasswordDialog && selectedUser && (
        <ResetPasswordDialog
          user={selectedUser}
          onClose={() => {
            setShowResetPasswordDialog(false);
            setSelectedUser(null);
          }}
          onSuccess={() => fetchUsers()}
        />
      )}
    </div>
  );
};

export default UserManagement;